import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import CountyDropdown from './CountyDropdown';
import AgencyDropdown from './AgencyDropdown';
import FacilityDropdown from './FacilityDropdown';
import { COUNTRIES } from '../constants/states';
import '../Style/App.css';

// Custom styles for view-only fields (greyed out)
const viewOnlyFieldStyle = {
  marginTop: '5px',
  padding: '6px 10px',
  fontSize: '14px',
  backgroundColor: '#f8f9fa',
  color: '#6c757d',
  border: '1px solid #e9ecef',
  borderRadius: '4px',
  cursor: 'not-allowed'
};

const DetentionEditModal = ({ record, visible, onClose, onUpdate, isViewMode = false }) => {
  const { showError } = useAlert();
  const [loading, setLoading] = useState(false);

  // Helper function to render checkbox fields conditionally
  const renderCheckbox = (name, checked, label, options = {}) => {
    const { onChange = handleInputChange, style = {} } = options;

    if (isViewMode) {
      return (
        <div className="checkboxLabel" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <input
            type="text"
            value={checked ? 'Yes' : 'No'}
            className="modern-input"
            style={{ backgroundColor: '#f8f9fa', color: '#6c757d', border: '1px solid #e9ecef', borderRadius: '4px', cursor: 'not-allowed', width: '60px', marginRight: '10px' }}
            readOnly
            disabled
          />
          <span>{label}</span>
        </div>
      );
    }

    return (
      <label className="checkboxLabel">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          style={style}
        />
        {label}
      </label>
    );
  };

  // Helper function to convert county names to proper case
  const toProperCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  // Utility function to convert database date (YYYY-MM-DD) to month input format (YYYY-MM)
  const formatDateForMonthInput = (dateString) => {
    if (!dateString) return '';

    // Handle both full date strings and already formatted month strings
    if (typeof dateString === 'string') {
      // If it's already in YYYY-MM format, return as is
      if (dateString.match(/^\d{4}-\d{2}$/)) {
        return dateString;
      }

      // If it's a full date (YYYY-MM-DD), extract year and month
      if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        return dateString.substring(0, 7); // Extract YYYY-MM
      }

      // Handle ISO date strings with time (YYYY-MM-DDTHH:mm:ss.sssZ)
      if (dateString.match(/^\d{4}-\d{2}-\d{2}T/)) {
        return dateString.substring(0, 7); // Extract YYYY-MM
      }
    }

    // Fallback: try to parse as date and format - FIXED for timezone issues
    try {
      // For older records, create date in local timezone to avoid UTC conversion
      const date = new Date(dateString + 'T12:00:00'); // Add noon time to avoid timezone issues
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
      }
    } catch (error) {
      // Error formatting date for month input - return empty string as fallback
    }

    return '';
  };

  // Utility function to convert month input format (YYYY-MM) to database date format (YYYY-MM-01)
  const formatMonthInputForDatabase = (monthString) => {
    if (!monthString) return '';

    // If it's already in YYYY-MM format, append -01
    if (monthString.match(/^\d{4}-\d{2}$/)) {
      return `${monthString}-01`;
    }

    return monthString;
  };

  // Utility function to format book out date for input - FIXED for timezone issues
  const formatBookOutDate = (dateString) => {
    if (!dateString) return '';

    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }

    // Handle ISO date strings with time (YYYY-MM-DDTHH:mm:ss.sssZ)
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}T/)) {
      return dateString.split('T')[0]; // Extract YYYY-MM-DD
    }

    // Fallback: try to parse as date and format - FIXED for timezone issues
    try {
      // For older records, create date in local timezone to avoid UTC conversion
      const date = new Date(dateString + 'T12:00:00'); // Add noon time to avoid timezone issues
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (error) {
      // Error formatting book out date - return empty string as fallback
    }

    return '';
  };

  const [formData, setFormData] = useState({
    // Standalone demographic fields (for standalone records)
    standaloneFirstName: '',
    standaloneLastName: '',
    standaloneDateOfBirth: '',
    standaloneSex: '',
    standaloneCountryOfCitizenship: '',
    standaloneArrestingAgency: '',

    reportMonth: '',
    facilityName: '',
    facilityType: 'COUNTY_JAIL',
    county: '',
    caseNumber: '',
    fbiNumber: '',

    // County Jail Data
    iceDetainerReceived: false,
    accompaniedByI200: false,
    accompaniedByI205: false,
    detainerLodged: false,
    detainerCancelledByIce: false,
    releasedIceTookCustody: false,
    iceFailedPickup: false,
    releasedDueToTimeout: false,
    highestOffenseClassification: '',
    releasedAfter48hrViolentCrime: false,
    releasedBeforeIceDetainer: false,
    jemDetainerBeforeIceMatch: false,
    transportedToIceOffice: false,
    housingCostReimbursement: false,
    iceReimbursementRate: '',
    bedDaysIncurred: '',
    isJuvenile: false,
    actualHousingCost: '',
    reimbursementDifference: '',
    releasedHousingCostsUpdates: false,

    // Release tracking fields
    bookOutDate: '',
    releasedMonth: '',

    // FDOC Data
    fdocConfirmedAlienWithDetainer: false,
    fdocConfirmedAlienWithoutDetainer: false,
    fdocIceDetainerRemoved: false,
    fdocReleasedToIceCustody: false,
    fdocScheduledRelease30Days: false,
    fdocReleasedIceRefused: false
  });

  // Populate form when record changes or modal becomes visible
  useEffect(() => {
    if (record && visible) {
      const formattedReportMonth = formatDateForMonthInput(record.report_month);
      const formattedBookOutDate = formatBookOutDate(record.book_out_date);
      const formattedReleasedMonth = record.released_month ? formatDateForMonthInput(record.released_month) : '';

      setFormData({
        // Standalone demographic fields (for standalone records)
        standaloneFirstName: record.standalone_first_name || '',
        standaloneLastName: record.standalone_last_name || '',
        standaloneDateOfBirth: record.standalone_date_of_birth ? record.standalone_date_of_birth.split('T')[0] : '',
        standaloneSex: record.standalone_sex || '',
        standaloneCountryOfCitizenship: record.standalone_country_of_citizenship || '',
        standaloneArrestingAgency: record.standalone_arresting_agency || '',

        reportMonth: formattedReportMonth,
        facilityName: record.facility_name || '',
        facilityType: record.facility_type || 'COUNTY_JAIL',
        county: toProperCase(record.county) || '',
        caseNumber: record.case_number || '',
        fbiNumber: record.fbi_number || '',

        // County Jail Data
        iceDetainerReceived: record.ice_detainer_received || false,
        accompaniedByI200: record.accompanied_by_i200 || false,
        accompaniedByI205: record.accompanied_by_i205 || false,
        detainerLodged: record.detainer_lodged || false,
        detainerCancelledByIce: record.detainer_cancelled_by_ice || false,
        releasedIceTookCustody: record.released_ice_took_custody || false,
        iceFailedPickup: record.ice_failed_48hr_pickup || false,
        releasedDueToTimeout: record.released_due_to_48hr_timeout || false,
        highestOffenseClassification: record.highest_offense_classification || '',
        releasedAfter48hrViolentCrime: record.released_after_48hr_violent_crime || false,
        releasedBeforeIceDetainer: record.released_before_ice_detainer || false,
        jemDetainerBeforeIceMatch: record.jem_detainer_before_ice_match || false,
        transportedToIceOffice: record.transported_to_ice_office || false,
        housingCostReimbursement: record.housing_cost_reimbursement_received || false,
        iceReimbursementRate: record.ice_reimbursement_rate || '',
        bedDaysIncurred: record.bed_days_incurred || '',
        isJuvenile: record.is_juvenile || false,
        actualHousingCost: record.actual_housing_cost || '',
        reimbursementDifference: record.reimbursement_difference || '',
        releasedHousingCostsUpdates: record.released_housing_costs_updates || false,

        // Release tracking fields - handle timezone issues consistently
        bookOutDate: formattedBookOutDate,
        releasedMonth: formattedReleasedMonth,

        // FDOC Data
        fdocConfirmedAlienWithDetainer: record.fdoc_confirmed_alien_with_detainer || false,
        fdocConfirmedAlienWithoutDetainer: record.fdoc_confirmed_alien_without_detainer || false,
        fdocIceDetainerRemoved: record.fdoc_ice_detainer_removed || false,
        fdocReleasedToIceCustody: record.fdoc_released_to_ice_custody || false,
        fdocScheduledRelease30Days: record.fdoc_scheduled_release_30_days || false,
        fdocReleasedIceRefused: record.fdoc_released_ice_refused || false
      });
    } else if (!visible) {
      // Reset form when modal is closed
      setFormData({
        // Standalone demographic fields (for standalone records)
        standaloneFirstName: '',
        standaloneLastName: '',
        standaloneDateOfBirth: '',
        standaloneSex: '',
        standaloneCountryOfCitizenship: '',
        standaloneArrestingAgency: '',

        reportMonth: '',
        facilityName: '',
        facilityType: 'COUNTY_JAIL',
        county: '',
        caseNumber: '',
        fbiNumber: '',

        // County Jail Data
        iceDetainerReceived: false,
        accompaniedByI200: false,
        accompaniedByI205: false,
        detainerLodged: false,
        detainerCancelledByIce: false,
        releasedIceTookCustody: false,
        iceFailedPickup: false,
        releasedDueToTimeout: false,
        highestOffenseClassification: '',
        releasedAfter48hrViolentCrime: false,
        releasedBeforeIceDetainer: false,
        jemDetainerBeforeIceMatch: false,
        transportedToIceOffice: false,
        housingCostReimbursement: false,
        iceReimbursementRate: '',
        bedDaysIncurred: '',
        isJuvenile: false,
        actualHousingCost: '',
        reimbursementDifference: '',
        releasedHousingCostsUpdates: false,

        // Release tracking fields
        bookOutDate: '',
        releasedMonth: '',

        // FDOC Data
        fdocConfirmedAlienWithDetainer: false,
        fdocConfirmedAlienWithoutDetainer: false,
        fdocIceDetainerRemoved: false,
        fdocReleasedToIceCustody: false,
        fdocScheduledRelease30Days: false,
        fdocReleasedIceRefused: false
      });
    }
  }, [record, visible]);

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Helper function to handle title case input changes for names
  const handleTitleCaseInputChange = (e) => {
    const { name, value } = e.target;
    const titleCaseValue = toTitleCase(value);
    const syntheticEvent = {
      target: {
        name: name,
        value: titleCaseValue,
        type: 'text'
      }
    };
    handleInputChange(syntheticEvent);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      let updated = {
        ...prev,
        [name]: newValue
      };

      // Auto-calculate housing costs when bed days or juvenile status changes
      if ((name === 'bedDaysIncurred' && value) || (name === 'isJuvenile')) {
        const bedDays = parseInt(name === 'bedDaysIncurred' ? value : updated.bedDaysIncurred) || 0;
        const isJuvenile = name === 'isJuvenile' ? checked : updated.isJuvenile;
        const dailyRate = isJuvenile ? 23.30 : 80.86; // Juvenile: $23.30, Adult: $80.86
        const iceReimbursement = 50.00; // Flat fee per person

        if (bedDays >= 0) {
          const actualHousingCost = bedDays * dailyRate;

          if (bedDays === 0) {
            // No reimbursement for 0 bed days
            updated = {
              ...updated,
              iceReimbursementRate: '0.00',
              actualHousingCost: actualHousingCost.toFixed(2),
              reimbursementDifference: '0.00'
            };
          } else {
            // Normal calculation for 1+ bed days
            const reimbursementDifference = actualHousingCost - iceReimbursement;

            updated = {
              ...updated,
              iceReimbursementRate: iceReimbursement.toFixed(2),
              actualHousingCost: actualHousingCost.toFixed(2),
              reimbursementDifference: reimbursementDifference.toFixed(2)
            };
          }
        }
      }

      // Pre-fill book out date and released month when "Released / Housing Costs Updated" is checked (only if fields are empty)
      if (name === 'releasedHousingCostsUpdates' && checked) {
        // Only pre-fill with current date/month if the fields are currently empty (no existing data)
        if (!updated.bookOutDate && !updated.releasedMonth) {
          const today = new Date();
          const currentBookOutDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
          const currentReleasedMonth = today.toISOString().slice(0, 7); // YYYY-MM format

          updated = {
            ...updated,
            bookOutDate: currentBookOutDate,
            releasedMonth: currentReleasedMonth
          };
        }
        // If there's existing data, don't overwrite it - just show the fields
      }

      // Clear book out date and released month when "Released / Housing Costs Updated" is unchecked
      if (name === 'releasedHousingCostsUpdates' && !checked) {
        updated = {
          ...updated,
          bookOutDate: '',
          releasedMonth: ''
        };
      }

      // Auto-populate released month when book out date changes
      if (name === 'bookOutDate' && value) {
        const releasedMonth = value.slice(0, 7); // Extract YYYY-MM from YYYY-MM-DD
        updated = {
          ...updated,
          releasedMonth: releasedMonth
        };
      }

      // Clear released month when book out date is cleared
      if (name === 'bookOutDate' && !value) {
        updated = {
          ...updated,
          releasedMonth: ''
        };
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.reportMonth || !formData.facilityName || !formData.facilityType) {
      showError('Please fill in all required fields (Report Month, Facility Name, and Facility Type)');
      return;
    }

    // Validate standalone demographic fields if this is a standalone record
    if (record && record.is_standalone_record) {
      if (!formData.standaloneFirstName || !formData.standaloneLastName || !formData.standaloneDateOfBirth || !formData.standaloneSex || !formData.standaloneCountryOfCitizenship) {
        showError('Please fill in all required demographic fields (First Name, Last Name, Date of Birth, Sex, and Country of Citizenship)');
        return;
      }
    }

    setLoading(true);
    try {
      // Convert the month format back to database format before sending
      const dataToSend = {
        ...formData,
        reportMonth: formatMonthInputForDatabase(formData.reportMonth)
      };

      await onUpdate(dataToSend);
    } catch (error) {
      showError('Error updating detention record: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Offense classification options (matching StandaloneDetentionForm)
  const offenseClassifications = [
    'Capital Felony',
    'Life Felony',
    '1st Degree Felony',
    '2nd Degree Felony',
    '3rd Degree Felony',
    '1st Degree Misdemeanor',
    '2nd Degree Misdemeanor',
    'Noncriminal Violation'
  ];

  if (!visible) return null;

  return (
    <div className="modalOverlay">
      <div className="modalContent" style={{ maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modalHeader">
          <h2>{isViewMode ? 'View Detention Record' : 'Edit Detention Record'}</h2>
          <button className="closeBtn" onClick={onClose}>&times;</button>
        </div>

        <div className="modalBody">
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <h3>Basic Information</h3>
            <div className="formRow">
              <div className="formGroup">
                <label><strong>Report Month *</strong></label>
                {isViewMode ? (
                  <input
                    type="text"
                    value={formData.reportMonth ? (() => {
                      const [year, month] = formData.reportMonth.split('-');
                      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                    })() : ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                ) : (
                  <input
                    type="month"
                    name="reportMonth"
                    value={formData.reportMonth}
                    onChange={handleInputChange}
                    className="modern-input"
                    required
                  />
                )}
              </div>
              <div className="formGroup">
                <label><strong>Facility Name *</strong></label>
                {isViewMode ? (
                  <input
                    type="text"
                    value={formData.facilityName}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                ) : (
                  <FacilityDropdown
                    name="facilityName"
                    value={formData.facilityName}
                    onChange={handleInputChange}
                    className="modern-input"
                    required
                  />
                )}
              </div>
            </div>

            <div className="formRow">
              <div className="formGroup">
                <label><strong>Facility Type *</strong></label>
                {isViewMode ? (
                  <input
                    type="text"
                    value={formData.facilityType === 'COUNTY_JAIL' ? 'County Jail' : 'FDOC'}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                ) : (
                  <select
                    name="facilityType"
                    value={formData.facilityType}
                    onChange={handleInputChange}
                    className="modern-input"
                    required
                  >
                    <option value="COUNTY_JAIL">County Jail</option>
                    <option value="FDOC">FDOC</option>
                  </select>
                )}
              </div>
              <div className="formGroup">
                <label><strong>County</strong></label>
                {isViewMode ? (
                  <input
                    type="text"
                    value={formData.county}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                ) : (
                  <CountyDropdown
                    name="county"
                    value={formData.county}
                    onChange={handleInputChange}
                    className="modern-input"
                  />
                )}
              </div>
            </div>

            <div className="formRow">
              <div className="formGroup">
                <label><strong>Case Number</strong></label>
                {isViewMode ? (
                  <input
                    type="text"
                    value={formData.caseNumber}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                ) : (
                  <input
                    type="text"
                    name="caseNumber"
                    value={formData.caseNumber}
                    onChange={handleInputChange}
                    className="modern-input"
                  />
                )}
              </div>

              <div className="formGroup">
                <label><strong>FBI Number</strong></label>
                {isViewMode ? (
                  <input
                    type="text"
                    value={formData.fbiNumber}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                ) : (
                  <input
                    type="text"
                    name="fbiNumber"
                    value={formData.fbiNumber}
                    onChange={handleInputChange}
                    className="modern-input"
                    placeholder="Enter FBI number (optional)"
                  />
                )}
              </div>
            </div>

            {/* Standalone Demographic Information - only show for standalone records */}
            {record && record.is_standalone_record && (
              <>
                <h3>Demographic Information</h3>
                <div className="formRow">
                  <div className="formGroup">
                    <label><strong>First Name *</strong></label>
                    {isViewMode ? (
                      <input
                        type="text"
                        value={formData.standaloneFirstName}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    ) : (
                      <input
                        type="text"
                        name="standaloneFirstName"
                        value={formData.standaloneFirstName}
                        onChange={handleTitleCaseInputChange}
                        className="modern-input"
                        required
                        placeholder="Enter first name"
                      />
                    )}
                  </div>
                  <div className="formGroup">
                    <label><strong>Last Name *</strong></label>
                    {isViewMode ? (
                      <input
                        type="text"
                        value={formData.standaloneLastName}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    ) : (
                      <input
                        type="text"
                        name="standaloneLastName"
                        value={formData.standaloneLastName}
                        onChange={handleTitleCaseInputChange}
                        className="modern-input"
                        required
                        placeholder="Enter last name"
                      />
                    )}
                  </div>
                </div>

                <div className="formRow">
                  <div className="formGroup">
                    <label><strong>Date of Birth *</strong></label>
                    {isViewMode ? (
                      <input
                        type="text"
                        value={formData.standaloneDateOfBirth ? new Date(formData.standaloneDateOfBirth).toLocaleDateString() : ''}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    ) : (
                      <input
                        type="date"
                        name="standaloneDateOfBirth"
                        value={formData.standaloneDateOfBirth}
                        onChange={handleInputChange}
                        className="modern-input"
                        required
                      />
                    )}
                  </div>
                  <div className="formGroup">
                    <label><strong>Sex *</strong></label>
                    {isViewMode ? (
                      <input
                        type="text"
                        value={formData.standaloneSex}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    ) : (
                      <select
                        name="standaloneSex"
                        value={formData.standaloneSex}
                        onChange={handleInputChange}
                        className="modern-input"
                        required
                      >
                        <option value="">Select Sex</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    )}
                  </div>
                </div>

                <div className="formRow">
                  <div className="formGroup">
                    <label><strong>Country of Citizenship *</strong></label>
                    {isViewMode ? (
                      <input
                        type="text"
                        value={formData.standaloneCountryOfCitizenship}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    ) : (
                      <select
                        name="standaloneCountryOfCitizenship"
                        value={formData.standaloneCountryOfCitizenship}
                        onChange={handleInputChange}
                        className="modern-input"
                        required
                      >
                        <option value="">Select Country</option>
                        {COUNTRIES.map(country => (
                          <option key={country.value} value={country.label}>{country.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="formGroup">
                    <label><strong>Arresting Agency</strong></label>
                    {isViewMode ? (
                      <input
                        type="text"
                        value={formData.standaloneArrestingAgency}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    ) : (
                      <AgencyDropdown
                        name="standaloneArrestingAgency"
                        value={formData.standaloneArrestingAgency}
                        onChange={handleInputChange}
                        className="modern-input"
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            {/* County Jail Data Section */}
            {formData.facilityType === 'COUNTY_JAIL' && (
              <>
                <h3>County Jail Data Collection</h3>

                {/* Item 1: ICE Detainer Forms */}
                <div className="formSubsection">
                  <h4>1. ICE Detainer Forms (I-247)</h4>
                  <div className="checkboxGroup">
                    {renderCheckbox('iceDetainerReceived', formData.iceDetainerReceived, 'ICE Detainer Received (I-247)')}

                    <div className="subCheckboxes">
                      {renderCheckbox('accompaniedByI200', formData.accompaniedByI200, 'Accompanied by I-200 (Probable Cause Arrest Warrant)')}
                      {renderCheckbox('accompaniedByI205', formData.accompaniedByI205, 'Accompanied by I-205 (Warrant of Removal)')}
                    </div>
                  </div>
                </div>

                {/* Item 2: Detainer Lodged and Cancelled */}
                <div className="formSubsection">
                  <h4>2. Detainer Status</h4>
                  <div className="checkboxGroup">
                    {renderCheckbox('detainerLodged', formData.detainerLodged, 'Hold Placed')}
                    {renderCheckbox('detainerCancelledByIce', formData.detainerCancelledByIce, 'Hold Cancelled by ICE')}
                    {renderCheckbox('releasedIceTookCustody', formData.releasedIceTookCustody, 'ICE Took Custody of Inmate')}
                  </div>
                </div>

                {/* Item 3: 48-Hour Period */}
                <div className="formSubsection">
                  <h4>3. 48-Hour ICE Custody Period</h4>
                  <div className="checkboxGroup">
                    {renderCheckbox('iceFailedPickup', formData.iceFailedPickup, 'ICE Failed to Take Custody Within 48 Hours')}
                    {renderCheckbox('releasedDueToTimeout', formData.releasedDueToTimeout, 'Released Due to 48-Hour Timeout')}
                  </div>
                </div>

                {/* Item 4: Offense Classification */}
                <div className="formSubsection">
                  <h4>4. Highest State Law Offense Classification</h4>
                  {isViewMode ? (
                    <input
                      type="text"
                      value={formData.highestOffenseClassification || ''}
                      className="modern-input"
                      style={viewOnlyFieldStyle}
                      readOnly
                      disabled
                    />
                  ) : (
                    <select
                      name="highestOffenseClassification"
                      value={formData.highestOffenseClassification}
                      onChange={handleInputChange}
                      className="modern-input"
                    >
                      <option value="">Select offense classification</option>
                      {offenseClassifications.map(classification => (
                        <option key={classification} value={classification}>
                          {classification}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Items 5-8: Additional County Jail Data */}
                <div className="formSubsection">
                  <h4>5-8. Additional Data</h4>
                  <div className="checkboxGroup">
                    {renderCheckbox('releasedAfter48hrViolentCrime', formData.releasedAfter48hrViolentCrime, 'Released After 48hr + Charged with Violent Crime')}
                    {renderCheckbox('releasedBeforeIceDetainer', formData.releasedBeforeIceDetainer, 'Released Before ICE Detainer Received')}
                    {renderCheckbox('jemDetainerBeforeIceMatch', formData.jemDetainerBeforeIceMatch, 'Jail Enforcement Model Detainer Before ICE Match')}
                    {renderCheckbox('transportedToIceOffice', formData.transportedToIceOffice, 'Transported to ICE Office by Detention')}
                  </div>
                </div>

                {/* Item 9: Housing Cost Data */}
                <div className="formSubsection">
                  <h4>9. Housing Cost Reimbursement Data</h4>
                  <div className="formRow">
                    <div className="formGroup">
                      <label>Bed Days Incurred *</label>
                      {isViewMode ? (
                        <input
                          type="text"
                          value={formData.bedDaysIncurred}
                          className="modern-input"
                          style={viewOnlyFieldStyle}
                          readOnly
                          disabled
                        />
                      ) : (
                        <input
                          type="number"
                          name="bedDaysIncurred"
                          value={formData.bedDaysIncurred}
                          onChange={handleInputChange}
                          className="modern-input"
                          placeholder="Enter number of bed days"
                          min="0"
                        />
                      )}
                      {!isViewMode && (
                        <small style={{ color: '#666', fontSize: '12px' }}>
                          Enter bed days to auto-calculate costs
                        </small>
                      )}
                    </div>

                    <div className="formGroup">
                      {renderCheckbox('isJuvenile', formData.isJuvenile, 'Juvenile (Under 18)', { style: { marginRight: '8px' } })}
                    </div>
                  </div>

                  <div className="formRow">
                    <div className="formGroup">
                      {renderCheckbox('releasedHousingCostsUpdates', formData.releasedHousingCostsUpdates, 'Released / Housing Costs Updated', { style: { marginRight: '8px' } })}
                    </div>
                  </div>

                  <>
                    <div className="formRow">
                      <div className="formGroup">
                        {renderCheckbox('housingCostReimbursement', formData.housingCostReimbursement, 'Housing Cost Reimbursement Received', { style: { marginRight: '8px' } })}
                      </div>
                    </div>

                    <div className="formRow">
                      <div className="formGroup">
                        <label><strong>Book Out Date</strong></label>
                        {isViewMode ? (
                          <input
                            type="text"
                            value={formData.bookOutDate ? (() => {
                              if (formData.bookOutDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                const [year, month, day] = formData.bookOutDate.split('-');
                                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                return date.toLocaleDateString();
                              }
                              return new Date(formData.bookOutDate + 'T12:00:00').toLocaleDateString();
                            })() : ''}
                            className="modern-input"
                            style={viewOnlyFieldStyle}
                            readOnly
                            disabled
                          />
                        ) : (
                          <input
                            type="date"
                            name="bookOutDate"
                            value={formData.bookOutDate}
                            onChange={handleInputChange}
                            className="modern-input"
                          />
                        )}
                        {!isViewMode && (
                          <small style={{ color: '#666', fontSize: '12px' }}>
                            Date when inmate was released
                          </small>
                        )}
                      </div>

                      <div className="formGroup">
                        <label><strong>Released Month</strong></label>
                        <input
                          type="text"
                          value={formData.releasedMonth ? (() => {
                            const [year, month] = formData.releasedMonth.split('-');
                            const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                          })() : ''}
                          className="modern-input"
                          style={viewOnlyFieldStyle}
                          readOnly
                          disabled
                        />
                        {!isViewMode && (
                          <small style={{ color: '#666', fontSize: '12px' }}>
                            Auto-calculated from Book Out Date
                          </small>
                        )}
                      </div>
                    </div>
                  </>

                  <div className="formRow">
                    <div className="formGroup">
                      <label>ICE Reimbursement Rate ($)</label>
                      {isViewMode ? (
                        <input
                          type="text"
                          value={formData.iceReimbursementRate}
                          className="modern-input"
                          style={viewOnlyFieldStyle}
                          readOnly
                          disabled
                        />
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          name="iceReimbursementRate"
                          value={formData.iceReimbursementRate}
                          onChange={handleInputChange}
                          className="modern-input"
                          placeholder="0.00"
                        />
                      )}
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Auto-calculated: $50.00 flat fee (if bed days > 0){!isViewMode && ', but can be manually adjusted'}
                      </small>
                    </div>

                    <div className="formGroup">
                      <label>Actual Housing Cost ($)</label>
                      {isViewMode ? (
                        <input
                          type="text"
                          value={formData.actualHousingCost}
                          className="modern-input"
                          style={viewOnlyFieldStyle}
                          readOnly
                          disabled
                        />
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          name="actualHousingCost"
                          value={formData.actualHousingCost}
                          onChange={handleInputChange}
                          className="modern-input"
                          placeholder="0.00"
                        />
                      )}
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Auto-calculated: Bed days × {formData.isJuvenile ? '$23.30' : '$80.86'}{!isViewMode && ', but can be manually adjusted'}
                      </small>
                    </div>
                  </div>

                  <div className="formRow">
                    <div className="formGroup">
                      <label>Reimbursement Difference ($)</label>
                      {isViewMode ? (
                        <input
                          type="text"
                          value={formData.reimbursementDifference}
                          className="modern-input"
                          style={viewOnlyFieldStyle}
                          readOnly
                          disabled
                        />
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          name="reimbursementDifference"
                          value={formData.reimbursementDifference}
                          onChange={handleInputChange}
                          className="modern-input"
                          placeholder="0.00"
                        />
                      )}
                      <small style={{ color: '#666', fontSize: '12px' }}>
                        Auto-calculated: Actual cost - ICE reimbursement{!isViewMode && ', but can be manually adjusted'}
                      </small>
                    </div>
                  </div>
                </div>


              </>
            )}

            {/* FDOC Data Section */}
            {formData.facilityType === 'FDOC' && (
              <div className="formSection">
                <h3>Florida Department of Corrections Data</h3>

                <div className="checkboxGroup">
                  {renderCheckbox('fdocConfirmedAlienWithDetainer', formData.fdocConfirmedAlienWithDetainer, '1. Confirmed Criminal Alien with ICE Detainer')}
                  {renderCheckbox('fdocConfirmedAlienWithoutDetainer', formData.fdocConfirmedAlienWithoutDetainer, '2. Confirmed Criminal Alien without ICE Detainer')}
                  {renderCheckbox('fdocIceDetainerRemoved', formData.fdocIceDetainerRemoved, '3. ICE Detainer Removed by ICE')}
                  {renderCheckbox('fdocReleasedToIceCustody', formData.fdocReleasedToIceCustody, '4. Released to ICE Custody')}
                  {renderCheckbox('fdocScheduledRelease30Days', formData.fdocScheduledRelease30Days, '5. Scheduled Release Within 30 Days')}
                  {renderCheckbox('fdocReleasedIceRefused', formData.fdocReleasedIceRefused, '6. Released to Community (ICE Refused Custody)')}
                </div>
              </div>
            )}

            <div className="modalFooter" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="modern-btn" onClick={onClose} disabled={loading}>
                {isViewMode ? 'Close' : 'Cancel'}
              </button>
              {!isViewMode && (
                <button type="submit" className="modern-btn" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Record'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DetentionEditModal;
